import { useEffect, useState } from 'react';
import { useAuth } from '@tsuyosa/auth';
import { getProfile, updateProfile } from '@/lib/api';
import type { FanUser } from '@tsuyosa/types';

export function ProfilePage() {
  const { accessToken, user: authUser } = useAuth();
  const [user, setUser]    = useState<FanUser | null>(null);
  const [isEditing, setEditing] = useState(false);
  const [form, setForm]    = useState({ display_name: '', bio: '' });
  const [isSaving, setSaving]  = useState(false);
  const [error, setError]  = useState('');
  const [success, setSuccess]  = useState('');

  useEffect(() => {
    if (!accessToken) return;
    getProfile(accessToken).then(u => {
      setUser(u);
      setForm({ display_name: u.display_name ?? '', bio: u.bio ?? '' });
    }).catch(() => {});
  }, [accessToken]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!accessToken) return;
    setError(''); setSuccess(''); setSaving(true);
    try {
      const updated = await updateProfile(accessToken, {
        display_name: form.display_name || undefined,
        bio: form.bio || undefined,
      });
      setUser(updated);
      setForm({ display_name: updated.display_name ?? '', bio: updated.bio ?? '' });
      setEditing(false);
      setSuccess('Profil berhasil diperbarui.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal menyimpan profil.');
    } finally { setSaving(false); }
  }

  const profile = user ?? authUser;

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <h1 className="mb-6 text-xl font-bold text-white">Profil Saya</h1>

      {/* Avatar placeholder */}
      <div className="mb-6 flex items-center gap-4">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#f472b6]/20 text-2xl font-bold text-[#f472b6]">
          {(profile?.display_name ?? profile?.username ?? '?')[0]?.toUpperCase()}
        </div>
        <div>
          <p className="font-semibold text-white">{profile?.display_name ?? profile?.username}</p>
          <p className="text-sm text-white/40">@{profile?.username}</p>
        </div>
      </div>

      {/* Info card */}
      {!isEditing ? (
        <div className="rounded-xl border border-white/5 bg-[#130d1a] p-6">
          {success && (
            <p className="mb-4 rounded-lg bg-green-500/10 px-4 py-3 text-sm text-green-400">
              {success}
            </p>
          )}
          <dl className="flex flex-col gap-4">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-white/30">Email</dt>
              <dd className="mt-0.5 text-white">{profile?.email}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-white/30">Username</dt>
              <dd className="mt-0.5 text-white">@{profile?.username}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-white/30">Nama Tampilan</dt>
              <dd className="mt-0.5 text-white">{profile?.display_name ?? <span className="text-white/30">—</span>}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-white/30">Bio</dt>
              <dd className="mt-0.5 text-white">{profile?.bio ?? <span className="text-white/30">—</span>}</dd>
            </div>
          </dl>
          <button
            onClick={() => setEditing(true)}
            className="mt-6 rounded-lg border border-[#f472b6]/40 px-5 py-2 text-sm font-medium text-[#f472b6] transition-colors hover:bg-[#f472b6]/10"
          >
            Edit Profil
          </button>
        </div>
      ) : (
        <form onSubmit={handleSave} className="rounded-xl border border-white/5 bg-[#130d1a] p-6">
          {error && (
            <p className="mb-4 rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-400">
              {error}
            </p>
          )}
          <div className="flex flex-col gap-4">
            <div>
              <label className="mb-1.5 block text-sm text-white/60">Nama Tampilan</label>
              <input
                type="text" value={form.display_name}
                onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder-white/30 outline-none focus:border-[#f472b6]/50 focus:ring-1 focus:ring-[#f472b6]/30"
                placeholder="Nama tampilan"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm text-white/60">Bio</label>
              <textarea
                value={form.bio} rows={3}
                onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder-white/30 outline-none focus:border-[#f472b6]/50 focus:ring-1 focus:ring-[#f472b6]/30"
                placeholder="Ceritakan sedikit tentang dirimu..."
              />
            </div>
          </div>
          <div className="mt-6 flex gap-3">
            <button
              type="submit" disabled={isSaving}
              className="flex items-center justify-center rounded-lg bg-[#f472b6] px-5 py-2 text-sm font-semibold text-white shadow-[0_0_12px_rgba(244,114,182,0.35)] transition-all hover:bg-[#ec4899] disabled:opacity-50"
            >
              {isSaving
                ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                : 'Simpan'
              }
            </button>
            <button
              type="button" onClick={() => { setEditing(false); setError(''); }}
              className="rounded-lg border border-white/10 px-5 py-2 text-sm text-white/60 transition-colors hover:text-white"
            >
              Batal
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
